import * as React from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/utils'

export type ModalSize = 'sm' | 'md' | 'lg'
export type ModalVariant = 'center' | 'sheet' | 'top'

export interface ModalProps {
  /** Steuert die Sichtbarkeit. */
  isOpen: boolean
  /** Wird bei ESC, Backdrop-Klick (sofern dismissible) und ✕ aufgerufen. */
  onClose: () => void
  /** Optionaler Titel im Header. Ohne Titel wird kein Header gerendert. */
  title?: React.ReactNode
  children: React.ReactNode
  /** Optionaler Footer-Bereich (z. B. Aktions-Buttons), klebt unten und scrollt nicht mit. */
  footer?: React.ReactNode
  /** max-width der zentrierten Variante. sm 380 · md 540 · lg 760 px. Default 'md'. */
  size?: ModalSize
  /** ESC + Backdrop-Klick + ✕ schließen (Default true). */
  dismissible?: boolean
  /**
   * 'center' (Default), 'sheet' (Bottom-Sheet, z. B. Kamera-Dialoge) oder 'top' (oben angeschlagen — für
   * Auswahl mit Suchfeld am Telefon: Die Bildschirmtastatur verdeckt sonst die gefilterte Liste).
   */
  variant?: ModalVariant
  /** Optionale Extra-Klassen für die Modal-Fläche. */
  className?: string
  /** ✕-Button im Header ausblenden (Default false). */
  hideCloseButton?: boolean
  /** Eigenes Padding des Body (Default true). false = Hülle für freeform-Inhalte. */
  padded?: boolean
  /** Body in scrollbaren Container legen (Default true). false = children steuern Layout/Scroll selbst (eigener flex-col). */
  scrollBody?: boolean
}

const sizeClass: Record<ModalSize, string> = {
  sm: 'max-w-[380px]',
  md: 'max-w-[540px]',
  lg: 'max-w-[760px]',
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

let openModalCount = 0

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  dismissible = true,
  variant = 'center',
  className,
  hideCloseButton = false,
  padded = true,
  scrollBody = true,
}: ModalProps) {
  const panelRef = React.useRef<HTMLDivElement>(null)
  const lastFocused = React.useRef<HTMLElement | null>(null)
  const titleId = React.useId()

  // Body-Scroll-Lock (zählt verschachtelte Modals mit).
  React.useEffect(() => {
    if (!isOpen) return
    openModalCount += 1
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      openModalCount -= 1
      if (openModalCount === 0) document.body.style.overflow = prevOverflow
    }
  }, [isOpen])

  // Fokus setzen beim Öffnen, beim Schließen zurückgeben.
  React.useEffect(() => {
    if (!isOpen) return
    lastFocused.current = document.activeElement as HTMLElement | null
    const panel = panelRef.current
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE)
    ;(first ?? panel)?.focus()
    return () => {
      lastFocused.current?.focus?.()
    }
  }, [isOpen])

  // ESC + Fokus-Falle.
  React.useEffect(() => {
    if (!isOpen) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && dismissible) {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab') return
      const panel = panelRef.current
      if (!panel) return
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      )
      if (items.length === 0) {
        e.preventDefault()
        panel.focus()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement
      if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [isOpen, dismissible, onClose])

  if (!isOpen) return null
  if (typeof document === 'undefined') return null

  const isSheet = variant === 'sheet'
  const isTop = variant === 'top'

  return createPortal(
    <div
      // Backdrop: blickdicht, Klick schließt (sofern dismissible).
      className={cn(
        'fixed inset-0 z-[1000] flex bg-black/75 p-4',
        isSheet ? 'items-end justify-center' : isTop ? 'items-start justify-center' : 'items-center justify-center',
      )}
      onMouseDown={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        // Inline-Background garantiert Blickdichtheit (unabhängig von Tailwind-Purge/Config).
        style={{ background: 'hsl(var(--popover))', color: 'hsl(var(--popover-foreground))' }}
        className={cn(
          // Solide, blickdichte Fläche über Popover-Token (Fallback Card via index.css).
          'flex w-full flex-col overflow-hidden bg-popover text-popover-foreground shadow-2xl outline-none',
          'border border-border',
          isSheet
            ? 'max-h-[90vh] rounded-t-lg'
            : isTop
              ? cn('max-h-[90vh] rounded-lg', sizeClass[size])
              : cn('max-h-[85vh] rounded-lg', sizeClass[size]),
          className,
        )}
      >
        {isSheet && (
          <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-muted-foreground/40" />
        )}

        {(title || !hideCloseButton) && (
          <div className="flex shrink-0 items-start gap-3 border-b border-border px-5 py-4">
            {title && (
              <h2 id={titleId} className="text-lg font-semibold leading-snug">
                {title}
              </h2>
            )}
            {!hideCloseButton && dismissible && (
              <button
                type="button"
                aria-label="Schließen"
                onClick={onClose}
                className="ml-auto rounded-md px-2 py-0.5 text-xl leading-none text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                &times;
              </button>
            )}
          </div>
        )}

        {scrollBody ? (
          <div className={cn('flex-1 overflow-y-auto', padded && 'px-5 py-4')}>{children}</div>
        ) : (
          children
        )}

        {footer && (
          <div className="flex shrink-0 justify-end gap-2.5 border-t border-border bg-popover px-5 py-3.5">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

Modal.displayName = 'Modal'
